import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  ClaimrWidgetType,
  IPostReferralArgs,
  IReferralLinkQueryArgs,
  IReferralLinkResponse,
  IRouletteEntity,
  ITotalBalanceResponse,
  TGetClaimrActivityParams,
  UserKarma,
  UserNftToken,
  UserRefLink,
} from "app/types";
import { IRunningLine } from "app/widgets/RunningLine/RunningLine";
import { Banner } from "app/widgets/banner/banner";
import tokenService from "services/token.service";

export const newApi = createApi({
  reducerPath: "applicationApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_SECONDARY,
    prepareHeaders: (headers, { endpoint }) => {
      const tokens = tokenService.tokens;
      const rawStorage = localStorage.getItem('authTokens');

      // Log for debugging auth issues
      if (endpoint === 'getUserKarma') {
        console.log('[API prepareHeaders - getUserKarma]', {
          hasTokens: !!tokens,
          hasRawStorage: !!rawStorage,
          tokenPreview: tokens?.accessToken ? `${tokens.accessToken.substring(0, 20)}...` : 'none',
          rawStoragePreview: rawStorage ? `${rawStorage.substring(0, 50)}...` : 'none',
        });

        if (rawStorage) {
          try {
            const parsed = JSON.parse(rawStorage);
            console.log('[API prepareHeaders - getUserKarma] Storage parsed:', {
              hasTokenData: !!parsed.tokenData,
              hasSavedAt: !!parsed.savedAt,
              savedAt: parsed.savedAt,
              ageInMs: Date.now() - parsed.savedAt,
              isExpired: (Date.now() - parsed.savedAt) > (3600000 * 2 * 365)
            });
          } catch (e) {
            console.error('[API prepareHeaders - getUserKarma] Failed to parse storage:', e);
          }
        }
      }

      if (tokens) {
        headers.set(
          "Authorization",
          `Bearer ${tokens.accessToken}`
        );
      } else if (endpoint === 'getUserKarma') {
        console.error('[API prepareHeaders - getUserKarma] No tokens available!');
      }
      return headers;
    },
    paramsSerializer: (params) => {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach((val) => searchParams.append(key, val));
        } else {
          searchParams.append(key, value);
        }
      });
      return searchParams.toString();
    },
  }),

  endpoints: (builder) => ({
    createApplication: builder.mutation({
      query: (application) => ({
        url: "/application",
        method: "POST",
        body: application,
      }),
    }),

    isEwwaUser: builder.query({
      query: ({ userId, idoId }) => ({
        url: "/application",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        body: { userId, idoId },
      }),
    }),

    findByIdWithStatus: builder.query({
      keepUnusedDataFor: 0,
      query: ({ idoId, address }) => ({
        url: "/ido/status",
        method: "POST",
        cache: "no-store",
        body: { idoId, address },
      }),
    }),

    getIdos: builder.query<
      any,
      { address?: string; page?: number; limit?: number }
    >({
      query: ({ address, page, limit }) => ({
        url: "/idos",
        method: "GET",
        params: {
          ...(address ? { address } : {}),
          page,
          limit,
        },
      }),
    }),

    findById: builder.query({
      keepUnusedDataFor: 0,
      query: (idoId) => ({
        url: `/ido/${idoId}`,
        method: "GET",
        cache: "no-store",
      }),
    }),

    getApplicationDetails: builder.query({
      query: ({ chainIds, idoId, address }) => ({
        url: `/application/details`,
        method: "POST",
        body: { chainIds, idoId, address },
      }),
    }),

    createSignature: builder.mutation({
      query: (body) => ({
        url: "/ido/signature",
        method: "POST",
        body,
      }),
    }),

    checkWhitelistStatus: builder.query({
      query: ({ idoId, address }) => ({
        url: "/application/check/wl",
        method: "POST",
        body: { idoId, address },
      }),
    }),

    getPurchaseDetails: builder.query({
      query: ({ chainIds, idoId, address }) => ({
        url: `/application/purchaseDetails`,
        method: "POST",
        body: { chainIds, idoId, address },
      }),
    }),

    getTotalBalance: builder.query<number, { idoId: string }>({
      query: ({ idoId }) => `/ido/totalBalance/${idoId}`,
    }),

    subscribeToWhitelist: builder.mutation<
      void,
      { email: string; listId: number }
    >({
      query: (body) => ({
        url: "/brevo/subscribe",
        method: "POST",
        body,
      }),
    }),

    authNonce: builder.mutation<
      { nonce: string; message: string },
      { address: string }
    >({
      query: (body) => ({
        url: "/auth/nonce",
        method: "POST",
        body,
      }),
    }),

    authenticate: builder.mutation<
      {
        accessToken: string;
        user: { createdAt: string; updatedAt: string; address: string };
      },
      { address: string; signature?: string; message?: string }
    >({
      query: (body) => ({
        url: "/auth",
        method: "POST",
        body,
      }),
    }),

    uploadExcel: builder.mutation({
      query: ({ file, body }) => {
        const formData = new FormData();
        formData.append("file", file);
        Object.keys(body).forEach((key) => formData.append(key, body[key]));
        return {
          url: "/application/excel",
          method: "POST",
          body: formData,
        };
      },
    }),

    trackCampaignData: builder.mutation<
      void,
      { address: string; campaignId: string }
    >({
      query: (body) => ({
        url: "/leads/create",
        method: "POST",
        body,
      }),
    }),

    userEvent: builder.mutation<void, { event: string; ethAddress: string }>({
      query: (body) => ({
        url: "/users/events",
        method: "POST",
        body,
      }),
    }),

    getClaimrToken: builder.query<ClaimrWidgetType, { wallet: string }>({
      query: ({ wallet }) => ({
        url: `/claimr/token`,
        method: "GET",
        params: { wallet },
      }),
    }),

    getUserKarma: builder.query<
      UserKarma,
      { account: string; campaignIds: string[] }
    >({
      query: ({ account, campaignIds }) => ({
        url: `/karma/karma-tokens`,
        method: "GET",
        params: { account, campaignIds },
      }),
    }),

    getReferralCode: builder.query<
      UserRefLink,
      { account: string; campaignId: string }
    >({
      query: ({ account, campaignId }) => ({
        url: `/claimr/ref_code`,
        method: "GET",
        params: { account, campaignId },
      }),
    }),

    getNftTokens: builder.query<UserNftToken, { userAddress: string }>({
      query: ({ userAddress }) => ({
        url: `/nft/tokens/${userAddress}`,
        method: "GET",
      }),
    }),

    getUserRefs: builder.query<
      number,
      { account: string; campaignIds: string[] }
    >({
      query: ({ account, campaignIds }) => ({
        url: `/claimr/user-refs`,
        method: "GET",
        params: { account, campaignIds },
      }),
    }),

    getUserLeaderboard: builder.query<
      { position: number; tier: string; ref_xp: number; address: string },
      { platform: string; address: string; campaignIds: string[] }
    >({
      query: (body) => ({
        url: `/karma/leaderboard/user`,
        method: "POST",
        body,
      }),
    }),

    getUsersByCompleteQuestXp: builder.query<any[], void>({
      query: () => ({
        url: `/karma/leaderboard`,
        method: "GET",
      }),
    }),

    getUsersByRefXp: builder.query<any[], void>({
      query: () => ({
        url: `/karma/referral-leaderboard`,
        method: "GET",
      }),
    }),

    getUpcomingIdos: builder.query({
      query: (params = { page: 0, limit: 10 }) => ({
        url: `/dashboard/upcoming`,
        method: "GET",
        params,
      }),
    }),

    getWhitelistedIdos: builder.query({
      query: (params = { page: 0, limit: 10 }) => ({
        url: `/dashboard/whitelisted`,
        method: "GET",
        params,
      }),
    }),

    getPastIdos: builder.query({
      query: (params = { page: 0, limit: 10 }) => ({
        url: `/dashboard/past`,
        method: "GET",
        params,
      }),
    }),

    getMindBalance: builder.query({
      query: () => "/mind/balance",
    }),

    getMindNft: builder.query({
      query: () => "/mind/nft",
    }),

    getLineXnft: builder.query({
      query: () => "/linex/nft",
    }),

    getClipFinanceBalance: builder.query({
      query: () => "/clipfinance/nft",
    }),

    getAlphapioner: builder.query({
      query: () => "/alphapioner/nft",
    }),

    getMrUniNft: builder.query({
      query: () => "/mr-uni/nft",
    }),

    getRefText: builder.query({
      query: ({ idoId }) => ({
        url: `/ido/${idoId}/ref/genetare_text`,
        method: "POST",
      }),
    }),

    getPromotionBanners: builder.query<Banner[], void>({
      query: () => ({
        url: `/ido/banners`,
        method: "GET",
      }),
    }),

    getPortalInfo: builder.query<any, string>({
      query: (address) => `/ido/portal/${address}`,
    }),

    getLatestPurchases: builder.query<IRunningLine[], string>({
      query: (idoId) => `/applications/${idoId}/latest`,
    }),

    postReferral: builder.mutation<any, IPostReferralArgs>({
      query: ({ refId, idoId }) => ({
        url: `/referrals/${refId}/${idoId}`,
        method: "POST",
      }),
    }),

    saveReferral: builder.mutation<{ status: string }, { refId: string }>({
      query: ({ refId }) => ({
        url: `/users/referral/${refId}`,
        method: "POST",
      }),
    }),

    getReferralFullLink: builder.query<
      IReferralLinkResponse,
      IReferralLinkQueryArgs
    >({
      query: ({ idoId, address, name, refPercent }) => ({
        url: "/referrals/link",
        params: {
          idoId,
          address,
          name,
          refPercent,
        },
      }),
    }),

    spinRoulette: builder.mutation<
      | {
          prize: {
            name: string;
            value: string;
            probability: number;
            type: string;
          };
          ticketId: string;
        }[]
      | {
          prize: {
            name: string;
            value: string;
            probability: number;
            type: string;
          };
          ticketId: string;
        },
      { rouletteId: string; bundleSize?: 5 | 10 }
    >({
      query: ({ rouletteId, bundleSize }) => ({
        url: `roulette/spin/${rouletteId}`,
        method: "GET",
        params: {
          ...(bundleSize && { bundleSize }),
        },
      }),
      transformResponse: (
        response:
          | {
              prize: {
                name: string;
                value: string;
                probability: number;
                type: string;
              };
              ticketId: string;
            }[]
          | {
              prize: {
                name: string;
                value: string;
                probability: number;
                type: string;
              };
              ticketId: string;
            }
      ) => {
        if (Array.isArray(response)) {
          return response;
        }
        return [response];
      },
    }),

    getAllRoulettes: builder.query<IRouletteEntity[], void>({
      query: () => "roulette",
    }),

    getRouletteById: builder.query<
      any,
      { rouletteId: string; address?: string }
    >({
      query: ({ rouletteId, address }) => {
        let queryString = `/roulette/get-roulette?rouletteId=${rouletteId}`;
        if (address) {
          queryString += `&address=${address}`;
        }
        return {
          url: queryString,
          method: "GET",
        };
      },
    }),

    processTx: builder.mutation({
      query: (body) => ({
        url: "/roulette/processTx",
        method: "POST",
        body,
      }),
    }),

    getClaimrActivity: builder.query<any, TGetClaimrActivityParams>({
      query: ({ platform, account, campaignIds }) => {
        const queryParams = new URLSearchParams({
          platform,
          account,
        });

        campaignIds.forEach((id: string) => {
          queryParams.append("campaignIds", id);
        });

        return {
          url: `/claimr/activity?${queryParams.toString()}`,
          method: "GET",
        };
      },
    }),

    getAward: builder.query<string[], string[]>({
      queryFn: async (body, _queryApi, _extraOptions, fetchWithBQ) => {
        const result = await fetchWithBQ({
          url: "roulette/award",
          method: "GET",
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as string[] };
      },
    }),

    getReferralStatistics: builder.query<
      any,
      { idoId: string; address?: string | null }
    >({
      query: ({ idoId, address }) => ({
        url: `referrals/statistics/${idoId}`,
        ...(address && { params: { address } }),
      }),
    }),

    getTotalIdoBalance: builder.query<ITotalBalanceResponse, string>({
      query: (idoId) => `/ido/totalBalance/${idoId}`,
    }),
  }),
});

export const {
  useCreateApplicationMutation,
  useSubscribeToWhitelistMutation,
  useCheckWhitelistStatusQuery,
  useFindByIdQuery,
  useFindByIdWithStatusQuery,
  useCreateSignatureMutation,
  useIsEwwaUserQuery,
  useGetIdosQuery,
  useUploadExcelMutation,
  useGetApplicationDetailsQuery,
  useGetPurchaseDetailsQuery,
  useGetTotalBalanceQuery,
  useAuthenticateMutation,
  useAuthNonceMutation,
  useTrackCampaignDataMutation,
  useUserEventMutation,
  useGetClaimrTokenQuery,
  useLazyGetClaimrTokenQuery,
  useGetUserKarmaQuery,
  useGetReferralCodeQuery,
  useGetNftTokensQuery,
  useGetUserRefsQuery,
  useGetUserLeaderboardQuery,
  useGetUsersByCompleteQuestXpQuery,
  useGetUsersByRefXpQuery,
  useGetUpcomingIdosQuery,
  useGetWhitelistedIdosQuery,
  useGetPastIdosQuery,
  useGetMindBalanceQuery,
  useGetMindNftQuery,
  useGetLineXnftQuery,
  useGetClipFinanceBalanceQuery,
  useGetAlphapionerQuery,
  useGetMrUniNftQuery,
  useSaveReferralMutation,
  useLazyGetRefTextQuery,
  useGetPromotionBannersQuery,
  useGetPortalInfoQuery,
  useGetLatestPurchasesQuery,
  usePostReferralMutation,
  useGetReferralFullLinkQuery,
  useSpinRouletteMutation,
  useGetAllRoulettesQuery,
  useGetRouletteByIdQuery,
  useProcessTxMutation,
  useGetClaimrActivityQuery,
  useLazyGetAwardQuery,
  useGetReferralStatisticsQuery,
  useGetTotalIdoBalanceQuery,
} = newApi;
